import type { CaptureKind } from '@mystiq/contracts';

/** A recording kept in this browser until Mystiq has it. */
export type StoredRecording = {
  id: string;
  workspaceId: string;
  /** Whose recording it is: never offered to another identity on this browser. */
  userKey: string;
  capture: CaptureKind;
  /** The item's title, e.g. "Voice note 23 Oct 2026, 21:57" (older recordings may lack it). */
  title?: string;
  filename: string;
  mimeType: string;
  startedAt: number;
  durationMs: number;
  /** One upload per recording: asking again after a lost connection continues the same upload. */
  uploadKey: string;
  /** 'recording' until stopped; a recording still 'recording' on the next visit was cut off. */
  state: 'recording' | 'stopped';
};

export type RecordingsStore = {
  create(recording: StoredRecording): Promise<void>;
  addChunk(id: string, seq: number, data: Blob): Promise<void>;
  update(id: string, changes: Partial<StoredRecording>): Promise<void>;
  list(workspaceId: string, userKey: string): Promise<StoredRecording[]>;
  /** The recorded audio so far, its pieces in order. */
  blob(id: string): Promise<Blob>;
  remove(id: string): Promise<void>;
};

const DB_NAME = 'mystiq-recordings';

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('Browser storage failed.'));
  });
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = tx.onerror = () => reject(tx.error ?? new Error('Browser storage failed.'));
  });
}

let opened: Promise<RecordingsStore | null> | null = null;

/**
 * The recordings kept in this browser (IndexedDB) while recording and until
 * each is saved to Mystiq, a second at a time, so closing the tab, a crash or
 * a lost connection does not lose what was recorded. Null when the browser
 * keeps nothing (some private windows): recording then lives in memory only.
 */
export function openRecordingsStore(): Promise<RecordingsStore | null> {
  opened ??= new Promise<RecordingsStore | null>((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, 1);
    } catch {
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      db.createObjectStore('recordings', { keyPath: 'id' });
      db.createObjectStore('chunks', { keyPath: ['recordingId', 'seq'] });
    };
    req.onerror = () => resolve(null);
    req.onsuccess = () => resolve(storeOver(req.result));
  });
  return opened;
}

function storeOver(db: IDBDatabase): RecordingsStore {
  return {
    async create(recording) {
      const tx = db.transaction('recordings', 'readwrite');
      tx.objectStore('recordings').put(recording);
      await done(tx);
    },
    async addChunk(id, seq, data) {
      const tx = db.transaction('chunks', 'readwrite');
      tx.objectStore('chunks').put({ recordingId: id, seq, data });
      await done(tx);
    },
    async update(id, changes) {
      const tx = db.transaction('recordings', 'readwrite');
      const store = tx.objectStore('recordings');
      const current = (await request(store.get(id))) as StoredRecording | undefined;
      if (current) store.put({ ...current, ...changes });
      await done(tx);
    },
    async list(workspaceId, userKey) {
      const all = (await request(db.transaction('recordings').objectStore('recordings').getAll())) as StoredRecording[];
      return all.filter((recording) => recording.workspaceId === workspaceId && recording.userKey === userKey).sort((a, b) => a.startedAt - b.startedAt);
    },
    async blob(id) {
      const tx = db.transaction(['recordings', 'chunks']);
      const recording = (await request(tx.objectStore('recordings').get(id))) as StoredRecording | undefined;
      const range = IDBKeyRange.bound([id, 0], [id, Number.MAX_SAFE_INTEGER]);
      const chunks = (await request(tx.objectStore('chunks').getAll(range))) as Array<{ data: Blob }>;
      return new Blob(chunks.map((chunk) => chunk.data), { type: recording?.mimeType ?? 'audio/webm' });
    },
    async remove(id) {
      const tx = db.transaction(['recordings', 'chunks'], 'readwrite');
      tx.objectStore('recordings').delete(id);
      tx.objectStore('chunks').delete(IDBKeyRange.bound([id, 0], [id, Number.MAX_SAFE_INTEGER]));
      await done(tx);
    },
  };
}
